// src/components/admin/page-management/section-forms/StaticContentForm.js
import React, { useState } from 'react';
import ColorPicker from '../../shared/ColorPicker';
import MediaLibrary from '../../shared/MediaUploader';

const StaticContentForm = ({ content, updateContent, pageSlug }) => {
  const [showMediaLibrary, setShowMediaLibrary] = useState(false);

  const handleSelectImage = (imageUrl) => {
    updateContent('imageUrl', imageUrl);
    setShowMediaLibrary(false);
  };

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold">Static Content Section</h3>
      
      {showMediaLibrary ? (
        <div className="bg-white rounded-lg shadow-lg p-4">
          <div className="flex justify-between items-center mb-4">
            <h4 className="text-lg font-medium">Media Library</h4>
            <button 
              onClick={() => setShowMediaLibrary(false)}
              className="text-gray-500 hover:text-gray-700"
            >
              Close
            </button>
          </div>
          <MediaLibrary 
            onSelect={handleSelectImage}
            pageSlug={pageSlug}
          />
        </div>
      ) : (
        <>
          {/* Content Settings */}
          <div className="bg-gray-50 p-4 rounded-md">
            <h4 className="font-medium mb-4">Content</h4>
            
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Title</label>
              <input
                type="text"
                value={content?.title || 'Discover Our Unique Packaging Solutions'}
                onChange={(e) => updateContent('title', e.target.value)}
                className="w-full p-2 border rounded"
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Description</label>
              <textarea
                value={content?.description || 'Experience creativity and innovation in every design. Our custom packaging solutions are crafted to elevate your brand\'s presence and make a lasting impression.'}
                onChange={(e) => updateContent('description', e.target.value)}
                className="w-full p-2 border rounded"
                rows="4"
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Text Color</label>
              <ColorPicker
                color={content?.textColor || '#ffffff'}
                onChange={(color) => updateContent('textColor', color)}
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Content Alignment</label>
              <select
                value={content?.contentAlignment || 'left'}
                onChange={(e) => updateContent('contentAlignment', e.target.value)}
                className="w-full p-2 border rounded"
              >
                <option value="left">Left</option>
                <option value="center">Center</option>
                <option value="right">Right</option>
              </select>
            </div>
          </div>
          
          {/* Button Settings */}
          <div className="bg-gray-50 p-4 rounded-md">
            <h4 className="font-medium mb-4">Button</h4>
            
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Button Text</label>
              <input
                type="text"
                value={content?.buttonText || 'Learn More'}
                onChange={(e) => updateContent('buttonText', e.target.value)}
                className="w-full p-2 border rounded"
              />
              <p className="text-xs text-gray-500 mt-1">Leave empty to hide the button</p>
            </div>
            
            {content?.buttonText && (
              <>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Button Link</label>
                  <input
                    type="text"
                    value={content?.buttonLink || '/aboutUs'}
                    onChange={(e) => updateContent('buttonLink', e.target.value)}
                    className="w-full p-2 border rounded"
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Button Color</label>
                    <ColorPicker
                      color={content?.buttonColor || '#2563eb'}
                      onChange={(color) => updateContent('buttonColor', color)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Button Text Color</label>
                    <ColorPicker
                      color={content?.buttonTextColor || '#ffffff'}
                      onChange={(color) => updateContent('buttonTextColor', color)}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
          
          {/* Image Settings */}
          <div className="bg-gray-50 p-4 rounded-md">
            <h4 className="font-medium mb-4">Image</h4>
            
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Image</label>
              <div className="flex items-center">
                <input
                  type="text"
                  value={content?.imageUrl || '/images/logo-static.png'}
                  onChange={(e) => updateContent('imageUrl', e.target.value)}
                  className="flex-1 p-2 border rounded"
                />
                <button
                  type="button"
                  onClick={() => setShowMediaLibrary(true)}
                  className="ml-2 px-3 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                >
                  Browse
                </button>
              </div>
              
              {content?.imageUrl && (
                <div className="mt-2 relative h-32 overflow-hidden rounded border">
                  <img 
                    src={content.imageUrl} 
                    alt="Preview" 
                    className="h-full w-auto object-contain mx-auto"
                  />
                </div>
              )}
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Image Alt Text</label>
              <input
                type="text"
                value={content?.imageAlt || 'Packaging'}
                onChange={(e) => updateContent('imageAlt', e.target.value)}
                className="w-full p-2 border rounded"
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Image Width</label>
              <input
                type="text"
                value={content?.imageWidth || '500px'}
                onChange={(e) => updateContent('imageWidth', e.target.value)}
                className="w-full p-2 border rounded"
                placeholder="e.g., 500px, 100%, 30rem"
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Image Radius</label>
              <input
                type="text"
                value={content?.imageRadius || '50px'}
                onChange={(e) => updateContent('imageRadius', e.target.value)}
                className="w-full p-2 border rounded"
                placeholder="e.g., 8px, 50px, 0"
              />
            </div>
            
            <div className="mb-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={content?.imageShadow !== false}
                  onChange={(e) => updateContent('imageShadow', e.target.checked)}
                  className="mr-2"
                />
                <span className="text-sm">Add Shadow to Image</span>
              </label>
            </div>
          </div>
          
          {/* Background Settings */}
          <div className="bg-gray-50 p-4 rounded-md">
            <h4 className="font-medium mb-4">Background</h4>
            
            <div className="mb-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={content?.backgroundGradient !== false}
                  onChange={(e) => updateContent('backgroundGradient', e.target.checked)}
                  className="mr-2"
                />
                <span className="text-sm">Use Gradient Background</span>
              </label>
            </div>
            
            {content?.backgroundGradient !== false ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Gradient Start Color</label>
                  <ColorPicker
                    color={content?.gradientFrom || '#3b82f6'}
                    onChange={(color) => updateContent('gradientFrom', color)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Gradient End Color</label>
                  <ColorPicker
                    color={content?.gradientTo || '#8b5cf6'}
                    onChange={(color) => updateContent('gradientTo', color)}
                  />
                </div>
              </div>
            ) : (
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Background Color</label>
                <ColorPicker
                  color={content?.backgroundColor || '#3b82f6'}
                  onChange={(color) => updateContent('backgroundColor', color)}
                />
              </div>
            )}
            
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Border Radius</label>
              <input
                type="text"
                value={content?.borderRadius || '0.5rem'}
                onChange={(e) => updateContent('borderRadius', e.target.value)}
                className="w-full p-2 border rounded"
                placeholder="e.g., 0.5rem, 8px, 0"
              />
            </div>
          </div>
          
          {/* Layout Settings */}
          <div className="bg-gray-50 p-4 rounded-md">
            <h4 className="font-medium mb-4">Layout</h4>
            
            <div className="mb-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={content?.reverseOnMobile === true}
                  onChange={(e) => updateContent('reverseOnMobile', e.target.checked)}
                  className="mr-2"
                />
                <span className="text-sm">Reverse Layout (Image First on Mobile)</span>
              </label>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium mb-1">Padding Top</label>
                <input
                  type="text"
                  value={content?.paddingTop || '2.5rem'}
                  onChange={(e) => updateContent('paddingTop', e.target.value)}
                  className="w-full p-2 border rounded"
                  placeholder="e.g., 2.5rem, 40px"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Padding Bottom</label>
                <input
                  type="text"
                  value={content?.paddingBottom || '2.5rem'}
                  onChange={(e) => updateContent('paddingBottom', e.target.value)}
                  className="w-full p-2 border rounded"
                  placeholder="e.g., 2.5rem, 40px"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium mb-1">Left Padding (Desktop)</label>
                <input
                  type="text"
                  value={content?.paddingLeftDesktop || '40px'}
                  onChange={(e) => updateContent('paddingLeftDesktop', e.target.value)}
                  className="w-full p-2 border rounded"
                  placeholder="e.g., 40px, 2.5rem"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Right Padding (Desktop)</label>
                <input
                  type="text"
                  value={content?.paddingRightDesktop || '0px'}
                  onChange={(e) => updateContent('paddingRightDesktop', e.target.value)}
                  className="w-full p-2 border rounded"
                  placeholder="e.g., 0px, 2rem"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium mb-1">Margin Top</label>
                <input
                  type="text"
                  value={content?.marginTop || '0.5rem'}
                  onChange={(e) => updateContent('marginTop', e.target.value)}
                  className="w-full p-2 border rounded"
                  placeholder="e.g., 0.5rem, 8px"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Margin Bottom</label>
                <input
                  type="text"
                  value={content?.marginBottom || '8rem'}
                  onChange={(e) => updateContent('marginBottom', e.target.value)}
                  className="w-full p-2 border rounded"
                  placeholder="e.g., 8rem, 128px"
                />
              </div>
            </div>
          </div>
          
          {/* Animation Settings */}
          <div className="bg-gray-50 p-4 rounded-md">
            <h4 className="font-medium mb-4">Animation</h4>
            
            <div className="mb-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={content?.animationEnabled !== false}
                  onChange={(e) => updateContent('animationEnabled', e.target.checked)}
                  className="mr-2"
                />
                <span className="text-sm">Enable Animation</span>
              </label>
            </div>
            
            {content?.animationEnabled !== false && (
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Animation Delay (seconds)</label>
                <input
                  type="number"
                  min="0"
                  step="0.1"
                  value={content?.animationDelay || '0'}
                  onChange={(e) => updateContent('animationDelay', e.target.value)}
                  className="w-full p-2 border rounded"
                />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default StaticContentForm;